/**
 * Display outputs (docs/06): separate output windows that mirror the frame or tile one large
 * virtual canvas across several screens, with bezel compensation. Layout math is pure; the
 * OutputManager handles the browser windows (Window Management API where available).
 */
import type { OutputConfig, Rect } from '../shared/messages';

export interface ScreenRect {
  id: string;
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface TileLayout {
  /** Size of the virtual canvas in CSS px (screens + bezel gaps). */
  width: number;
  height: number;
  /** Normalized region of the virtual canvas for each screen id. */
  regions: Map<string, Rect>;
}

/**
 * Lay screens out by their real positions, inserting a bezel gap between neighbours so imagery
 * "continues behind" the bezels rather than being squeezed.
 */
export function computeTiles(screens: ScreenRect[], bezelPx: number): TileLayout {
  if (screens.length === 0) return { width: 1, height: 1, regions: new Map() };
  const xs = [...new Set(screens.map((s) => s.left))].sort((a, b) => a - b);
  const ys = [...new Set(screens.map((s) => s.top))].sort((a, b) => a - b);
  const placed = screens.map((s) => ({
    s,
    x: s.left - xs[0]! + xs.indexOf(s.left) * bezelPx,
    y: s.top - ys[0]! + ys.indexOf(s.top) * bezelPx,
  }));
  const width = Math.max(...placed.map((p) => p.x + p.s.width));
  const height = Math.max(...placed.map((p) => p.y + p.s.height));
  const regions = new Map<string, Rect>();
  for (const p of placed) {
    regions.set(p.s.id, {
      x: p.x / width,
      y: p.y / height,
      w: p.s.width / width,
      h: p.s.height / height,
    });
  }
  return { width, height, regions };
}

/** Manual grid layout (no Window Management API): rows × cols of equally sized tiles. */
export function gridRegion(index: number, rows: number, cols: number, bezelFraction = 0): Rect {
  const r = Math.floor(index / cols) % rows;
  const c = index % cols;
  const gx = bezelFraction;
  const w = (1 - gx * (cols - 1)) / cols;
  const h = (1 - gx * (rows - 1)) / rows;
  return { x: c * (w + gx), y: r * (h + gx), w, h };
}

export const FULL_FRAME: Rect = { x: 0, y: 0, w: 1, h: 1 };

export type OutputMode = 'mirror' | 'tile';

export interface OutputInfo {
  id: string;
  label: string;
  screenId: string | null;
  width: number;
  height: number;
  config: OutputConfig;
  fullscreen: boolean;
}

interface ScreenDetailed {
  label?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  devicePixelRatio: number;
  isPrimary?: boolean;
}

export interface OutputHost {
  addOutput(
    id: string,
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    config: OutputConfig,
  ): void;
  updateOutput(
    id: string,
    patch: { width?: number; height?: number; config?: Partial<OutputConfig> },
  ): void;
  removeOutput(id: string): void;
  /** Keyboard events from output windows (so hotkeys work wherever focus is). */
  onKey(e: KeyboardEvent, type: 'down' | 'up'): void;
  /** Outputs changed (for the UI and for tile-mode render size). */
  onChange(): void;
}

interface OpenOutput extends OutputInfo {
  win: Window;
  dpr: number;
  cleanup: () => void;
}

/** Opens and manages output windows. */
export class OutputManager {
  readonly outputs = new Map<string, OpenOutput>();
  mode: OutputMode = 'mirror';
  bezelPx = 0;
  gridRows = 1;
  gridCols = 2;
  screens: ScreenRect[] = [];
  private screenDetails: { screens: ScreenDetailed[] } | null = null;
  private counter = 0;

  constructor(
    private readonly host: OutputHost,
    private readonly outputUrl: string,
  ) {}

  get windowManagement(): boolean {
    return typeof window !== 'undefined' && 'getScreenDetails' in window;
  }

  /** Ask for multi-screen permission and list screens (Chromium). */
  async detectScreens(): Promise<ScreenRect[]> {
    if (!this.windowManagement) return [];
    try {
      const details = await (
        window as unknown as {
          getScreenDetails(): Promise<{
            screens: ScreenDetailed[];
            addEventListener(t: string, f: () => void): void;
          }>;
        }
      ).getScreenDetails();
      this.screenDetails = details;
      details.addEventListener(
        'screenschange',
        () => void this.detectScreens().then(() => this.host.onChange()),
      );
      this.screens = details.screens.map((s, i) => ({
        id: `screen-${i}`,
        label: s.label || `Screen ${i + 1}${s.isPrimary ? ' (primary)' : ''}`,
        left: s.left,
        top: s.top,
        width: s.width,
        height: s.height,
      }));
      return this.screens;
    } catch {
      return [];
    }
  }

  /** Open an output window, on a specific screen when the Window Management API allows. */
  open(screenId: string | null = null): string | null {
    const id = `out${++this.counter}`;
    const detailed =
      screenId && this.screenDetails
        ? this.screenDetails.screens[Number(screenId.split('-')[1])]
        : undefined;
    const features = detailed
      ? `popup,left=${detailed.availLeft},top=${detailed.availTop},width=${detailed.availWidth},height=${detailed.availHeight}`
      : 'popup,width=960,height=540';
    const win = window.open(`${this.outputUrl}#${id}`, id, features);
    if (!win) return null;
    const label = String(this.outputs.size + 1);
    const info: OpenOutput = {
      id,
      label,
      screenId,
      width: 960,
      height: 540,
      dpr: 1,
      fullscreen: false,
      win,
      cleanup: () => undefined,
      config: { region: FULL_FRAME, rotation: 0, brightness: 1, pattern: 'none', label },
    };
    this.outputs.set(id, info);
    const attach = () => this.attach(info);
    if (win.document.readyState === 'complete' && win.document.querySelector('canvas')) attach();
    else win.addEventListener('load', attach, { once: true });
    this.host.onChange();
    return id;
  }

  private attach(o: OpenOutput): void {
    const doc = o.win.document;
    const canvas = doc.querySelector('canvas');
    if (!canvas) return;
    const measure = () => {
      o.dpr = Math.min(2, o.win.devicePixelRatio || 1);
      o.width = Math.round(o.win.innerWidth * o.dpr);
      o.height = Math.round(o.win.innerHeight * o.dpr);
      o.fullscreen = !!doc.fullscreenElement;
    };
    measure();
    this.applyLayout();
    this.host.addOutput(o.id, canvas, o.width, o.height, o.config);
    const onResize = () => {
      measure();
      this.host.updateOutput(o.id, { width: o.width, height: o.height });
      this.host.onChange();
    };
    const onKeyDown = (e: KeyboardEvent) => this.host.onKey(e, 'down');
    const onKeyUp = (e: KeyboardEvent) => this.host.onKey(e, 'up');
    o.win.addEventListener('resize', onResize);
    doc.addEventListener('fullscreenchange', onResize);
    o.win.addEventListener('keydown', onKeyDown);
    o.win.addEventListener('keyup', onKeyUp);
    const closedPoll = setInterval(() => {
      if (o.win.closed) this.close(o.id);
    }, 500);
    o.win.addEventListener('pagehide', () => this.close(o.id));
    o.cleanup = () => clearInterval(closedPoll);
    this.host.onChange();
  }

  close(id: string): void {
    const o = this.outputs.get(id);
    if (!o) return;
    o.cleanup();
    this.outputs.delete(id);
    this.host.removeOutput(id);
    if (!o.win.closed) o.win.close();
    this.relabel();
    this.applyLayout();
    this.host.onChange();
  }

  closeAll(): void {
    for (const id of [...this.outputs.keys()]) this.close(id);
  }

  setMode(mode: OutputMode): void {
    this.mode = mode;
    this.applyLayout();
    this.host.onChange();
  }

  setBezel(px: number): void {
    this.bezelPx = Math.max(0, Math.min(400, px));
    this.applyLayout();
    this.host.onChange();
  }

  setGrid(rows: number, cols: number): void {
    this.gridRows = Math.max(1, Math.min(4, rows));
    this.gridCols = Math.max(1, Math.min(6, cols));
    this.applyLayout();
    this.host.onChange();
  }

  configure(id: string, patch: Partial<OutputConfig>): void {
    const o = this.outputs.get(id);
    if (!o) return;
    o.config = { ...o.config, ...patch };
    this.host.updateOutput(id, { config: patch });
    this.host.onChange();
  }

  identify(ms = 2500): void {
    for (const o of this.outputs.values()) this.configure(o.id, { pattern: 'identify' });
    setTimeout(() => {
      for (const o of this.outputs.values())
        if (o.config.pattern === 'identify') this.configure(o.id, { pattern: 'none' });
    }, ms);
  }

  /**
   * The virtual canvas size (CSS px) the renderer should produce: the tile layout in tile mode,
   * otherwise null (render at the control window's size).
   */
  virtualSize(): { width: number; height: number } | null {
    if (this.mode !== 'tile' || this.outputs.size === 0) return null;
    const layout = this.tileLayout();
    if (layout) return { width: layout.width, height: layout.height };
    const first = [...this.outputs.values()][0]!;
    const w = first.width / first.dpr;
    const h = first.height / first.dpr;
    return {
      width: w * this.gridCols + this.bezelPx * (this.gridCols - 1),
      height: h * this.gridRows + this.bezelPx * (this.gridRows - 1),
    };
  }

  private tileLayout(): TileLayout | null {
    const assigned = [...this.outputs.values()].filter((o) => o.screenId);
    if (assigned.length === 0 || assigned.length !== this.outputs.size) return null;
    const rects = assigned
      .map((o) => this.screens.find((s) => s.id === o.screenId))
      .filter((s): s is ScreenRect => !!s);
    if (rects.length !== assigned.length) return null;
    return computeTiles(rects, this.bezelPx);
  }

  private applyLayout(): void {
    const outs = [...this.outputs.values()];
    if (this.mode === 'mirror') {
      for (const o of outs) this.setRegion(o, FULL_FRAME);
      return;
    }
    const layout = this.tileLayout();
    if (layout) {
      for (const o of outs) this.setRegion(o, layout.regions.get(o.screenId!) ?? FULL_FRAME);
      return;
    }
    const size = this.virtualSize();
    const bezelFraction = size ? this.bezelPx / size.width : 0;
    outs.forEach((o, i) =>
      this.setRegion(o, gridRegion(i, this.gridRows, this.gridCols, bezelFraction)),
    );
  }

  private setRegion(o: OpenOutput, region: Rect): void {
    const same =
      o.config.region.x === region.x &&
      o.config.region.y === region.y &&
      o.config.region.w === region.w &&
      o.config.region.h === region.h;
    if (same) return;
    o.config = { ...o.config, region };
    this.host.updateOutput(o.id, { config: { region } });
  }

  private relabel(): void {
    [...this.outputs.values()].forEach((o, i) => {
      o.label = String(i + 1);
      o.config = { ...o.config, label: o.label };
      this.host.updateOutput(o.id, { config: { label: o.label } });
    });
  }

  list(): OutputInfo[] {
    return [...this.outputs.values()].map(
      ({ id, label, screenId, width, height, config, fullscreen }) => ({
        id,
        label,
        screenId,
        width,
        height,
        config,
        fullscreen,
      }),
    );
  }
}
